// ── Inbox — Conversation actions (star, archive, unread, delete) ──
//
// Four window-pinned handlers (the chat header buttons call them by
// name via inline onclick attributes) plus the shared `_convAction`
// HTTP helper that POSTs to /.netlify/functions/conversation-action.
//
// State touched:
//   - the open conversation, read via getActiveConv
//   - the shared conversation list (messages/conversation-store.js): star,
//     archive, "marked unread", the unread count and taking a conversation
//     out of the list all go through the store's writes
//   - parent helpers: applyFilter, updateChatHeaderActions,
//     renderConversationsList, closeActiveConv
//   - the number of people waiting is the store's: convStore.notify()
//     rewrites it after a change
//
// All 4 methods are returned and the parent re-pins them on window.*
// (no module-level side effects from this file).

import { getSupabase } from '../config.js';
import { showConfirmDialog } from '../modals.js';
import { showToast } from '../utils.js';
import { escHtml } from './format-helpers.js';
import { deleteConversationWarningLines } from './delete-conversation-warning.js';
import * as convStore from '../messages/conversation-store.js';

/**
 * @param {object} deps
 * @param {() => any | null} deps.getActiveConv — returns the currently-open conv
 * @param {() => void} deps.applyFilter — re-runs filter logic into _filteredConversations
 * @param {() => void} deps.updateChatHeaderActions
 * @param {() => void} deps.renderConversationsList
 * @param {() => string} deps.getActiveFilter
 */
export function createConversationActions(deps) {
  const {
    getActiveConv,
    applyFilter,
    updateChatHeaderActions,
    renderConversationsList,
    getActiveFilter,
  } = deps;

  // The server's own words for the last refusal, e.g. "Only the account owner
  // can delete a conversation." Null after a success.
  let lastRefusal = null;

  async function convAction(action, clientId, groupId) {
    lastRefusal = null;
    try {
      const supabase = getSupabase();
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch('/.netlify/functions/conversation-action', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ action, clientId, groupId }),
      });
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        lastRefusal = json.error || null;
        throw new Error('Failed');
      }
      return true;
    } catch { return false; }
  }

  // ⚠️ A REFUSED ACTION SAYS WHY. It used to vanish without a word, so a team
  // member who pressed Delete saw nothing happen at all. Only the four buttons
  // below call this; the quiet "mark read" when a conversation opens does not.
  function sayItFailed() {
    showToast(lastRefusal || 'That did not work. Try again.', 'error');
  }

  async function starConversation() {
    const conv = getActiveConv();
    if (!conv) return;
    const action = conv.starred ? 'unstar' : 'star';
    const ok = await convAction(action, conv.clientId, conv.groupId);
    if (ok) {
      convStore.setStarred(conv, !conv.starred);
      updateChatHeaderActions();
      applyFilter();
      renderConversationsList();
    } else {
      sayItFailed();
    }
  }

  async function archiveConversation() {
    const conv = getActiveConv();
    if (!conv) return;
    const action = conv.archived ? 'unarchive' : 'archive';
    const ok = await convAction(action, conv.clientId, conv.groupId);
    if (ok) {
      convStore.setArchived(conv, !conv.archived);
      if (conv.archived && getActiveFilter() !== 'archived') {
        // Remove from view + close conversation
        convStore.remove(conv);
        window.closeInboxConversation();
        showToast('Conversation archived', 'success');
      } else {
        updateChatHeaderActions();
      }
      applyFilter();
      renderConversationsList();
      convStore.notify();
    } else {
      sayItFailed();
    }
  }

  async function markConversationUnread() {
    const conv = getActiveConv();
    if (!conv) return;
    const ok = await convAction('mark-unread', conv.clientId, conv.groupId);
    if (ok) {
      convStore.setManuallyUnread(conv, true);
      convStore.setUnread(conv, Math.max(conv.unreadCount || 0, 1));
      window.closeInboxConversation();
      applyFilter();
      renderConversationsList();
      convStore.notify();
    } else {
      sayItFailed();
    }
  }

  function deleteConversation() {
    const conv = getActiveConv();
    if (!conv) return;
    // ⚠️ The server's delete is permanent, erases the client's in-app chat too,
    // and cancels every message waiting to send to them, automatic ones
    // included. The words live in delete-conversation-warning.js
    // beside the reasons; each line is escaped here because the names are typed by
    // people. First line bold, the last ("cannot be undone") on its own line.
    const [question, ...rest] = deleteConversationWarningLines(conv).map((line) => escHtml(line));
    showConfirmDialog({
      message: [`<strong>${question}</strong>`, ...rest].join('<br><br>'),
      okLabel: 'Delete',
      okClass: 'btn-danger',
      onConfirm: async () => {
        const ok = await convAction('delete', conv.clientId, conv.groupId);
        if (ok) {
          convStore.remove(conv);
          window.closeInboxConversation();
          applyFilter();
          renderConversationsList();
          convStore.notify();
          showToast('Conversation deleted', 'success');
        } else {
          sayItFailed();
        }
      },
    });
  }

  return {
    convAction,
    starConversation,
    archiveConversation,
    markConversationUnread,
    deleteConversation,
  };
}
